import { useState, useCallback } from 'react';
import { s3baseUrl } from 'src/config';

// Token: #attachment_path{url} - stores only URL, name is looked up from attachments array
const ATTACHMENT_TOKEN_REGEX = /#attachment_path\{([^}]+)\}/g;

// ** Formats text containing user_id mentions and #attachment_path tokens into displayable HTML (Shamroz).
const useMentions = (team = [], attachments = [], projectTeam = []) => {
    const [mentionAnchorEl, setMentionAnchorEl] = useState(null);
    const [hoveredUser, setHoveredUser] = useState(null);

    const displayMentionName = useCallback((text) => {
        if (!text || typeof text !== 'string') return text;

        // 1. Replace #attachment_path{url} tokens - find name from attachments array by URL
        let result = text.replace(ATTACHMENT_TOKEN_REGEX, (match, url) => {
            // Find attachment in the attachments array by matching URL
            const attachment = attachments.find((a) => {
                const fullPath = a.attachment_path?.startsWith('http')
                    ? a.attachment_path
                    : s3baseUrl + a.attachment_path;
                // Try exact match first
                if (fullPath === url) return true;
                // Try matching just the path portion (in case URL formats differ)
                if (url.includes(a.attachment_path)) return true;
                // Try matching if stored URL contains the attachment path
                if (a.attachment_path && url.endsWith(a.attachment_path)) return true;
                return false;
            });

            // Use attachment_name from array if found, otherwise show full URL
            const displayName = attachment?.attachment_name || url;

            return `<a class="attachment-mention" href="${url}" target="_blank" rel="noopener noreferrer" style="color: #0060c8; font-weight: 500; text-decoration: underline; cursor: pointer;">${displayName}</a>`;
        });

        // 2. Replace @user_id{userId} and @everyone{everyone} tokens
        const mentionRegex = /@(?:everyone\{(everyone)\}|user_id\{([^}]+)\})/g;
        result = result.replace(mentionRegex, (match, everyoneId, userId) => {
            if (everyoneId === 'everyone') {
                return `<span class="mention" data-id="everyone" style="color: #0060c8; font-weight: 500; cursor: text; user-select:text;">@everyone</span>`;
            }
            let user = team.find(
                (u) => u.user_id && u.user_id.toLowerCase() === userId.toLowerCase()
            );
            if (!user && projectTeam && projectTeam.length > 0) {
                user = projectTeam.find(
                    (u) => u.user_id && u.user_id.toLowerCase() === userId.toLowerCase()
                );
            }
            if (user) {
                const displayName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
                return `<span class="mention" data-id="${userId}" style="color: #0060c8; font-weight: 500; cursor: text; user-select:text;">@${displayName}</span>`;
            }
            return match;
        });

        return result;
    }, [team, attachments, projectTeam]);

    const handleMentionHover = (event) => {
        if (event.buttons !== 0) return;
        if (window.getSelection()?.type === "Range") return;
        const target = event.target.closest(".mention");
        if (target) {
            const userId = target.getAttribute("data-id");
            let user = team.find((u) => String(u.user_id) === userId);
            if (!user && projectTeam && projectTeam.length > 0) {
                user = projectTeam.find((u) => String(u.user_id) === userId);
            }
            if (user) {
                setHoveredUser(user);
                setMentionAnchorEl(target);
            }
        } else {
            setMentionAnchorEl(null);
        }
    };

    // Handle click on attachment-mention spans inside the editor (opens in new tab)
    const handleAttachmentClick = (event) => {
        const target = event.target.closest(".attachment-mention");
        if (target) {
            const url = target.getAttribute("data-url") || target.getAttribute("href");
            if (url) {
                window.open(url, "_blank", "noopener,noreferrer");
            }
        }
    };

    const handleMentionLeave = () => {
        setMentionAnchorEl(null);
    };

    const getUserById = (userId) => {
        let user = team.find((u) => u.user_id === userId);
        if (!user && projectTeam && projectTeam.length > 0) {
            user = projectTeam.find((u) => u.user_id === userId);
        }
        return user;
    };

    return {
        displayMentionName,
        getUserById,
        handleMentionHover,
        handleMentionLeave,
        handleAttachmentClick,
        mentionAnchorEl,
        hoveredUser,
        team,
    };
};

export default useMentions;
